import React, { CSSProperties, useEffect, useMemo, useRef } from 'react';
import classes from './CardDragLayer.module.scss';
import { PostItemFragment } from '../../../../models/graphql';
import { CardOrientationEnum } from '../../../../components/cards/BasePostCard/BasePostCard.view';
import { DragLayerMonitor, useDragLayer } from 'react-dnd';
import { Void } from '../../../../models/basic-objects';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';
import { PostScheduleViewContext } from '../PostSchedule.controller';
import { BasePostCardLandscapeView } from '../../../../components/cards/BasePostCard/BasePostCardLandscape.view';

export type CardDragLayerViewProps = {
  orientation: CardOrientationEnum;
  scheduleItems: PostItemFragment[];
  hasSidebar: boolean;
  cardTime: string;
  scrollHandler: Void;
  isProfilePausedForCategory: boolean;
};

export type PostCardDragType = {
  id: string;
  startPosition: number;
  height: number;
  width: number;
  post: PostItemFragment;
  orientation: CardOrientationEnum;
  isStacked: boolean;
};

export const CardDragLayerView = (props: CardDragLayerViewProps): JSX.Element => {
  const { orientation, cardTime } = props;
  const handleRef = useRef<HTMLDivElement>(null);

  const { itemType, item, currentOffset, isDragging } = useDragLayer((monitor: DragLayerMonitor) => ({
    item: monitor.getItem() as PostCardDragType & { cardRef?: HTMLDivElement },
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getSourceClientOffset()
  }));

  const x = useMemo(() => {
    return props.hasSidebar ? currentOffset?.x || 0 : currentOffset?.x;
  }, [currentOffset?.x, props.hasSidebar]);
  const y = useMemo(() => {
    return (currentOffset?.y || 0) - 150;
  }, [currentOffset?.y]);

  //  Monitors mouse position and dragging state to handle automatic scrolling.
  const { scrollHandler } = props;
  useEffect(() => {
    const { innerHeight } = window;
    let scrollInterval: number | undefined;

    if (isDragging && y < 0) {
      scrollInterval = window.setInterval(() => {
        scrollHandler('UP');
      }, 100);
    } else if (isDragging && y > innerHeight - 300) {
      scrollInterval = window.setInterval(() => {
        scrollHandler('DOWN');
      }, 100);
    } else {
      if (scrollInterval) clearInterval(scrollInterval);
    }

    return () => {
      if (scrollInterval) clearInterval(scrollInterval); // cleanup function
    };
  }, [currentOffset, isDragging, scrollHandler, y]);

  const transform = useMemo(() => `translate(${x}px, ${y}px) rotate(+2deg)`, [x, y]);

  if (itemType !== DraggableTypesObject.POST_CARD || !isDragging) return <></>;

  // A card will never be smaller than 400x140, so this will be the base default
  const { width, height } = item.cardRef?.getBoundingClientRect() || { width: 400, height: 140 };
  const layerStyles: CSSProperties = {
    width,
    height,
    transform: transform
  };

  const { post } = item;
  const itemPost = {
    ...post,
    ...{ calculatedLocalDateTime: cardTime }
  };

  return (
    <div className={classes.CardDragLayer} style={{ ...layerStyles }}>
      <BasePostCardLandscapeView
        context={PostScheduleViewContext.LIST}
        index={0} // index can be zero because we just use this for the drag
        isDraggable={false}
        hideCheckbox={true}
        orientation={orientation}
        post={itemPost}
        menuListItems={[]}
        ref={handleRef}
      />
    </div>
  );
};
